import api from "@/service/api";
import { randomCallQueueRef } from "@/firebase";
import { getRandomString } from "@/utility/random";
import {
    onValue,
    off,
    child,
    set,
    remove,
    onDisconnect,
} from "firebase/database";

// kept outside vuex state — these are non-serializable Firebase handles
// (mirrors the firebaseStorage / pusherStorage pattern used elsewhere)
const queueDisconnectStorage = {
    ref: null,
    onDisconnect: null,
};

const FREE_PREVIEW_SECONDS_FALLBACK = 15;

const state = {
    inQueue: false,
    role: null,
    pricePerMinute: null,
    minutesRemaining: 0,
    freePreviewSeconds: FREE_PREVIEW_SECONDS_FALLBACK,
    previewLimit: 36,
    skip: {
        count: 0,
        freeAllowance: 20,
        paidCost: 1,
    },
    creators: {},
    customers: {},
    queueListenersAttached: false,
    currentRandomCall: null,
    matchedFromUser: null,
    matchedToUser: null,
    incomingMatch: null,
    showCordovaDialog: false,
    rating: {
        showing: false,
        randomCallId: null,
    },
    uniqueBillingKey: null,
    chargeFailed: false,
    hasRemoteVideoTrack: false,
    isCameraOn: false,
};

const getters = {
    oppositeQueue(state) {
        if (state.role === "creators") {
            return Object.values(state.customers || {});
        }
        return Object.values(state.creators || {});
    },
    oppositeCount(state, getters) {
        return getters.oppositeQueue.length;
    },
};

const mutations = {
    setLobby(state, lobby) {
        state.inQueue = lobby?.inQueue ?? false;
        state.role = lobby?.role ?? null;
        state.pricePerMinute = lobby?.pricePerMinute ?? null;
        state.minutesRemaining = lobby?.minutesRemaining ?? 0;
        state.freePreviewSeconds = lobby?.freePreviewSeconds ?? FREE_PREVIEW_SECONDS_FALLBACK;
        state.previewLimit = lobby?.previewLimit ?? 36;
        if (lobby?.skip) {
            state.skip = lobby.skip;
        }
    },
    setInQueue(state, value) {
        state.inQueue = value;
    },
    setCreators(state, snapshot) {
        state.creators = snapshot || {};
    },
    setCustomers(state, snapshot) {
        state.customers = snapshot || {};
    },
    setQueueListenersAttached(state, value) {
        state.queueListenersAttached = value;
    },
    setCurrentRandomCall(state, payload) {
        state.currentRandomCall = payload?.randomCall ?? null;
        state.matchedFromUser = payload?.fromUser ?? null;
        state.matchedToUser = payload?.toUser ?? null;
    },
    clearCurrentRandomCall(state) {
        state.currentRandomCall = null;
        state.matchedFromUser = null;
        state.matchedToUser = null;
        state.uniqueBillingKey = null;
        state.chargeFailed = false;
        state.hasRemoteVideoTrack = false;
        state.isCameraOn = false;
    },
    setIncomingMatch(state, payload) {
        state.incomingMatch = payload;
    },
    clearIncomingMatch(state) {
        state.incomingMatch = null;
    },
    setShowCordovaDialog(state, value) {
        state.showCordovaDialog = value;
    },
    setRating(state, payload) {
        state.rating = {
            showing: !!payload?.showing,
            randomCallId: payload?.randomCallId ?? null,
        };
    },
    setSkipState(state, skip) {
        if (skip) {
            state.skip = skip;
        }
    },
    setUniqueBillingKey(state, value) {
        state.uniqueBillingKey = value;
    },
    setChargeFailed(state, value) {
        state.chargeFailed = !!value;
    },
    setHasRemoteVideoTrack(state, value) {
        state.hasRemoteVideoTrack = !!value;
    },
    setIsCameraOn(state, value) {
        state.isCameraOn = !!value;
    },
};

const actions = {
    async fetchLobby({ commit }) {
        try {
            const { data } = await api.post("/randomCall/lobby");
            commit("setLobby", data);
        } catch (e) {
            // fall through; lobby state stays at defaults
        }
    },

    subscribeQueue({ state, commit }) {
        if (state.queueListenersAttached) {
            return;
        }

        const creatorsRef = child(randomCallQueueRef, "creators");
        const customersRef = child(randomCallQueueRef, "customers");

        onValue(creatorsRef, (snapshot) => {
            commit("setCreators", snapshot.val());
        });

        onValue(customersRef, (snapshot) => {
            commit("setCustomers", snapshot.val());
        });

        commit("setQueueListenersAttached", true);
    },

    unsubscribeQueue({ state, commit }) {
        if (!state.queueListenersAttached) {
            return;
        }

        off(child(randomCallQueueRef, "creators"));
        off(child(randomCallQueueRef, "customers"));

        commit("setCreators", {});
        commit("setCustomers", {});
        commit("setQueueListenersAttached", false);
    },

    async enterQueue({ commit, dispatch, rootState }) {
        if (rootState.system?.cordova) {
            commit("setShowCordovaDialog", true);
            return;
        }

        try {
            // server-side gating only: balance check, role resolution, payload
            // construction. backend does NOT write to Firebase — the FE owns
            // the queue write so its own connection drives the disconnect
            // cleanup via onDisconnect().
            const { data } = await api.post("/randomCall/enterQueue");

            const role = data?.role;
            const userId = rootState.onboarding?.user?.id;
            const payload = data?.queuePayload;

            if (!role || !userId || !payload) {
                throw new Error("enterQueue: missing role/userId/payload");
            }

            const queueRef = child(randomCallQueueRef, `${role}/${userId}`);

            // register the disconnect handler BEFORE the write so there is no
            // window where the entry exists without the auto-cleanup hook.
            queueDisconnectStorage.ref = queueRef;
            queueDisconnectStorage.onDisconnect = onDisconnect(queueRef);
            await queueDisconnectStorage.onDisconnect.remove();

            await set(queueRef, payload);

            commit("setInQueue", true);
            await dispatch("findMatch");
        } catch (e) {
            commit("setInQueue", false);
            throw e;
        }
    },

    async leaveQueue({ commit, rootState }) {
        // cancel the disconnect handler so it doesn't fire after a clean leave
        if (queueDisconnectStorage.onDisconnect) {
            try {
                await queueDisconnectStorage.onDisconnect.cancel();
            } catch (e) {
                // ignore
            }
            queueDisconnectStorage.onDisconnect = null;
        }

        // FE owns the entry; remove it directly. backend leaveQueue is still
        // called as belt-and-suspenders in case rules ever permit only the
        // server to delete.
        if (queueDisconnectStorage.ref) {
            try {
                await remove(queueDisconnectStorage.ref);
            } catch (e) {
                // ignore
            }
            queueDisconnectStorage.ref = null;
        } else {
            // fallback for stale state — try by current user id + role anyway
            const role = rootState.randomCall?.role;
            const userId = rootState.onboarding?.user?.id;
            if (role && userId) {
                try {
                    await remove(child(randomCallQueueRef, `${role}/${userId}`));
                } catch (e) {
                    // ignore
                }
            }
        }

        try {
            await api.post("/randomCall/leaveQueue");
        } catch (e) {
            // best-effort
        }
        commit("setInQueue", false);
    },

    async findMatch({ commit }) {
        try {
            const { data } = await api.post("/randomCall/match");

            if (data?.matched) {
                commit("setCurrentRandomCall", {
                    randomCall: data.randomCall,
                    fromUser: data.fromUser,
                    toUser: data.toUser,
                });
                commit("setInQueue", false);
                return data;
            }

            return data;
        } catch (e) {
            return null;
        }
    },

    charge({ commit, state, rootState }) {
        if (rootState.system?.cordova) {
            return;
        }

        const randomCall = state.currentRandomCall;
        if (!randomCall) {
            return;
        }

        const uniqueBillingKey = state.uniqueBillingKey
            || (() => {
                const key = getRandomString(32);
                commit("setUniqueBillingKey", key);
                return key;
            })();

        api.post(`/randomCall/${randomCall.id}/charge`, {
            uniqueBillingKey,
        }).then((response) => {
            if (response?.data?.randomCall) {
                commit("setCurrentRandomCall", {
                    randomCall: response.data.randomCall,
                    fromUser: state.matchedFromUser,
                    toUser: state.matchedToUser,
                });
            }
            commit("setChargeFailed", false);
        }).catch((error) => {
            if (error?.response?.status !== 402) {
                commit("setChargeFailed", true);
                api.post("/security/event", { code: "rccf" });
            }
        });
    },

    async hangup({ state, commit, dispatch }) {
        const randomCall = state.currentRandomCall;
        if (!randomCall) return;

        try {
            await api.post(`/randomCall/${randomCall.id}/hangup`);
        } catch (e) {
            // ignore
        }

        commit("setRating", { showing: true, randomCallId: randomCall.id });
        commit("clearCurrentRandomCall");

        // auto-rejoin queue per interface.md §4.6
        await dispatch("enterQueue");
    },

    async skip({ state, commit, dispatch }) {
        const randomCall = state.currentRandomCall;
        if (!randomCall) return;

        const { data } = await api.post(`/randomCall/${randomCall.id}/skip`);
        if (data?.skip) {
            commit("setSkipState", data.skip);
        }

        commit("clearCurrentRandomCall");
        await dispatch("enterQueue");
    },

    async decline({ state, commit }) {
        const randomCall = state.currentRandomCall;
        if (!randomCall) return;

        try {
            await api.post(`/randomCall/${randomCall.id}/decline`);
        } catch (e) {
            // ignore
        }
        commit("clearCurrentRandomCall");
    },

    async answer({ state, commit }) {
        const randomCall = state.currentRandomCall;
        if (!randomCall) return;

        try {
            const { data } = await api.post(`/randomCall/${randomCall.id}/answer`);
            if (data?.randomCall) {
                commit("setCurrentRandomCall", {
                    randomCall: data.randomCall,
                    fromUser: state.matchedFromUser,
                    toUser: state.matchedToUser,
                });
            }
        } catch (e) {
            // ignore
        }
    },

    async submitRating({ state, commit }, rating) {
        if (!state.rating.randomCallId) return;

        try {
            await api.post(`/randomCall/${state.rating.randomCallId}/rate`, { rating });
        } catch (e) {
            // ignore
        }
        commit("setRating", { showing: false });
    },

    skipRating({ commit }) {
        commit("setRating", { showing: false });
    },

    async nukeSkips({ dispatch }) {
        try {
            await api.post("/randomCall/nukeSkips");
            await dispatch("findMatch");
        } catch (e) {
            // ignore
        }
    },

    async handleMatched({ commit, dispatch }, data) {
        try {
            const { data: response } = await api.post(`/randomCall/${data.randomCallId}/get`);
            commit("setCurrentRandomCall", {
                randomCall: response.randomCall,
                fromUser: response.fromUser,
                toUser: response.toUser,
            });
            commit("setInQueue", false);
            dispatch("answer");
        } catch (e) {
            // ignore
        }
    },

    handleEnded({ state, commit }, data) {
        if (state.currentRandomCall?.id === data.randomCallId) {
            commit("setRating", { showing: true, randomCallId: data.randomCallId });
            commit("clearCurrentRandomCall");
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
